import { useCallback, useEffect, useMemo, useState } from 'react';
import { List, Map as ImmutableMap } from 'immutable';
import hubHttpClient from 'hub-http/clients/apiClient';
import { callIfPossible } from 'UIComponents/core/Functions';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import DocumentsPopoverForm from './DocumentsPopoverForm';
import FormattedMessage from 'I18n/components/FormattedMessage';
import DocumentsPopoverEmptyState from './DocumentsPopoverEmptyState';
import { fetchDocuments } from './fetchDocuments';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import { renderDocument } from './renderDocuments';
import { getBrandId } from '../../utils/documentUtils';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
function _buildSelectData(documents) {
  return Object.values(documents).reduce((documentList, document) => {
    return documentList.push(ImmutableMap({
      value: `${document.contentId}`,
      text: document.name
    }));
  }, List()).sortBy(document => document.get('name')).toJS();
}
const DocumentsPopover = ({
  contactEmail,
  outputType = contentOutputs.RENDERED,
  onChange,
  onClose,
  onFetchDocumentsError,
  showIncludeLinkPreview = true,
  onToggleAllowDownload
}) => {
  const [hasFetchedDocuments, setHasFetchedDocuments] = useState(false);
  const [documentsSearchResults, setDocumentsSearchResults] = useState({});
  const [totalDocuments, setTotalDocuments] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [linkText, setLinkText] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [requireEmail, setRequireEmail] = useState(true);
  const [includeLinkPreview, setIncludeLinkPreview] = useState(false);
  const [allowDownload, setAllowDownload] = useState(true);
  const [linkInfo, setLinkInfo] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(undefined);
  const handleDeckSelect = useCallback(e => {
    const newId = parseInt(e.target.value, 10);
    if (newId !== documentId) {
      const oldDeck = documentId ? documentsSearchResults[documentId] : undefined;
      const newDeck = documentsSearchResults[newId];
      const textIsTitle = oldDeck !== undefined && linkText === oldDeck.metadata.title;
      if (linkText === null || textIsTitle) {
        setLinkText(newDeck.metadata.title);
      }
      setDocumentId(newId);
      setDocumentName(newDeck.metadata.title);
      setPreviewTitle(newDeck.metadata.title);
      setPreviewImage(newDeck.metadata.thumbnailUrl);
      setSelectedBrandId(getBrandId(newDeck.description));
    }
  }, [documentId, linkText, documentsSearchResults]);
  const handleTextChange = useCallback(({
    target
  }) => {
    const {
      value
    } = target;
    if (linkText !== value && value.length < 300) {
      setLinkText(value);
    }
  }, [linkText]);
  const toggleRequireEmail = useCallback(() => {
    setRequireEmail(oldState => !oldState);
  }, []);
  const toggleLinkPreview = useCallback(() => {
    setIncludeLinkPreview(oldState => !oldState);
  }, []);
  const toggleAllowDownload = useCallback(() => {
    setAllowDownload(oldState => {
      const newValue = !oldState;
      onToggleAllowDownload === null || onToggleAllowDownload === void 0 || onToggleAllowDownload(newValue);
      return newValue;
    });
  }, [onToggleAllowDownload]);
  const handleClose = useCallback(() => {
    callIfPossible(onClose);
  }, [onClose]);
  const buildSelectData = useCallback(() => {
    if (totalDocuments === 0) {
      return [];
    }
    return _buildSelectData(documentsSearchResults);
  }, [documentsSearchResults, totalDocuments]);
  const renderDisplayText = useMemo(() => {
    return /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.documents.decksModal.linkTextLabel"
      }),
      children: /*#__PURE__*/_jsx(TextInput, {
        "data-test-id": "display-text",
        onChange: handleTextChange,
        value: linkText
      })
    });
  }, [handleTextChange, linkText]);
  const handleConfirm = useCallback((closePopover = true) => {
    onChange({
      documentId: documentId,
      documentName: documentName,
      displayText: linkText,
      requireEmail,
      includeLinkPreview,
      previewTitle,
      previewImage,
      linkInfo,
      allowDownload
    });
    if (closePopover) {
      handleClose();
    }
  }, [handleClose, onChange, documentId, documentName, linkText, requireEmail, includeLinkPreview, previewTitle, previewImage, linkInfo, allowDownload]);
  useEffect(() => {
    fetchDocuments({
      httpClient: hubHttpClient
    }).then(({
      results,
      total
    }) => {
      const searchResults = results.reduce((acc, result) => {
        acc[result.contentId] = result;
        return acc;
      }, {});
      setDocumentsSearchResults(searchResults);
      setTotalDocuments(total);
      setHasFetchedDocuments(true);
    }).catch(err => {
      setHasFetchedDocuments(true);
      callIfPossible(onFetchDocumentsError, err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!(outputType === contentOutputs.RENDERED && documentId && contactEmail)) return;
    renderDocument({
      httpClient: hubHttpClient,
      contactEmail,
      contentId: documentId,
      requireEmail,
      allowDownload
    }).then(renderedLink => {
      setLinkInfo(renderedLink);
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [contactEmail, outputType, documentId, requireEmail, allowDownload]);
  if (!hasFetchedDocuments) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        width: '400px'
      },
      "data-test-id": "documents-popover",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 350
      })
    });
  }
  if (hasFetchedDocuments && totalDocuments === 0) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        width: '400px'
      },
      "data-test-id": "documents-popover",
      children: /*#__PURE__*/_jsx(DocumentsPopoverEmptyState, {})
    });
  }
  const deckOptions = buildSelectData();
  return /*#__PURE__*/_jsx(DocumentsPopoverForm, {
    selectedDeckId: documentId,
    selectedBrandId: selectedBrandId,
    requireEmail: requireEmail,
    deckOptions: deckOptions,
    displayText: renderDisplayText,
    includeLinkPreview: includeLinkPreview,
    toggleLinkPreview: toggleLinkPreview,
    allowDownload: allowDownload,
    toggleAllowDownload: toggleAllowDownload,
    handleDeckSelect: handleDeckSelect,
    setDocumentsSearchResults: setDocumentsSearchResults,
    toggleRequireEmail: toggleRequireEmail,
    handleConfirm: handleConfirm,
    handleCancel: handleClose,
    showIncludeLinkPreview: showIncludeLinkPreview
  });
};
export default DocumentsPopover;